import { useEffect, useState } from 'react';
import { TelegramConnect } from '../components/notifications/TelegramConnect';
import { Button } from '../components/ui/button';
import { DateInput } from '../components/ui/date-input';
import { Input } from '../components/ui/input';
import { PhoneInput } from '../components/ui/phone-input';
import { useAuth } from '../hooks/useAuth';
import { useProfile, useUpdateProfile } from '../hooks/useProfile';
import { useAuthModal } from '../providers';

export function ProfilePage() {
  const { data: user, isLoading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const authModal = useAuthModal();

  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [patronymic, setPatronymic] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthDateValid, setBirthDateValid] = useState(true);
  const [attempted, setAttempted] = useState(false);

  // Якщо профіль ще не збережений (наприклад, перший вхід), але акаунт має
  // ім'я з Google — підставляємо орієнтовний розбір цього імені як підказку.
  // Це лише чернетка в полях, у профіль вона потрапить тільки після
  // натискання "ЗБЕРЕГТИ".
  useEffect(() => {
    if (profile) {
      setLastName(profile.lastName);
      setFirstName(profile.firstName);
      setPatronymic(profile.patronymic ?? '');
      setPhone(profile.phone);
      setBirthDate(profile.birthDate ?? '');
      return;
    }
    if (profile === null && user?.name) {
      const [guessedFirstName, ...rest] = user.name.trim().split(/\s+/);
      setFirstName(guessedFirstName ?? '');
      setLastName(rest.join(' '));
    }
  }, [profile, user]);

  if (authLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;

  if (!user) {
    return (
      <div className="section-pad text-center">
        <p className="mb-4 text-[15px] text-muted">Увійдіть, щоб редагувати особисті дані.</p>
        <Button variant="accent" size="lg" onClick={() => authModal.open('login')}>
          УВІЙТИ
        </Button>
      </div>
    );
  }

  if (profileLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;

  const canSave =
    lastName.trim().length > 0 && firstName.trim().length > 0 && phone.length > 0 && birthDateValid;

  const handleSubmit = () => {
    setAttempted(true);
    if (!canSave) return;
    updateProfile.mutate({
      lastName: lastName.trim(),
      firstName: firstName.trim(),
      patronymic: patronymic.trim() || null,
      phone,
      birthDate: birthDate || null,
    });
  };

  return (
    <div className="section-pad">
      <h1 className="m-0 mb-8 text-[clamp(32px,6vw,72px)] font-extrabold tracking-[-0.045em]">
        Особисті дані
      </h1>

      <div className="max-w-[420px] space-y-3">
        <Input value={user.email} disabled className="opacity-60" />

        <div>
          <Input
            placeholder="Прізвище"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={attempted && !lastName.trim() ? 'border-warn' : ''}
          />
          {attempted && !lastName.trim() && (
            <p className="mt-1 text-[12px] text-warn">Вкажіть прізвище</p>
          )}
        </div>

        <div>
          <Input
            placeholder="Імʼя"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={attempted && !firstName.trim() ? 'border-warn' : ''}
          />
          {attempted && !firstName.trim() && (
            <p className="mt-1 text-[12px] text-warn">Вкажіть імʼя</p>
          )}
        </div>

        <Input
          placeholder="По батькові (необовʼязково)"
          value={patronymic}
          onChange={(e) => setPatronymic(e.target.value)}
        />

        <div>
          <PhoneInput
            value={phone}
            onChange={setPhone}
            className={attempted && !phone ? 'border-warn' : ''}
          />
          {attempted && !phone && <p className="mt-1 text-[12px] text-warn">Вкажіть телефон</p>}
        </div>

        <DateInput value={birthDate} onChange={setBirthDate} onValidityChange={setBirthDateValid} />

        {updateProfile.isError && (
          <p className="text-[13px] text-warn">{updateProfile.error.message}</p>
        )}
        {updateProfile.isSuccess && <p className="text-[13px] text-accent">Збережено.</p>}

        <Button
          variant="accent"
          size="lg"
          className="w-full"
          disabled={updateProfile.isPending}
          onClick={handleSubmit}
        >
          ЗБЕРЕГТИ
        </Button>

        <div className="space-y-3 border-t border-line-soft pt-6">
          <p className="mono-label">СПОВІЩЕННЯ</p>
          <p className="text-[13px] text-muted">
            Листи про статус замовлень надходять на {user.email}.
          </p>
          <TelegramConnect />
        </div>
      </div>
    </div>
  );
}
